import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, Observable, Subscription, tap } from 'rxjs';
import produce from 'immer';
import {
  ActivateUser,
  ClearAuthStateSubscription,
  FetchIpAddress,
  FetchPortalUser,
  InitUser,
  resendActivationEmail,
  ResetState,
  ResetUser,
  createUserFromEmail,
  SetAgentAuthValues,
  SetAuthenticatedUser,
  SetIsActivating,
  SetUserSignInFlag,
  SignInTestPatient,
  SignOut,
  SwitchPatient,
  UpdatePassword,
  verifyActivationToken,
} from './user.actions';
import { ApiStoreData, defaultApiStoreData, MissingApiRequestDataError } from '../utils';
import { AuthenticatedUser, buildUpdatePasswordRequest, UserRole } from '../../models';
import { ApiService, SentryService, IpAddress, SessionExpirationService, IpAddressService } from '../../services';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, OktaAuth, UserClaims } from '@okta/okta-auth-js';
import { Router } from '@angular/router';
import { StateResetAll } from 'ngxs-reset-plugin';
import { AppState } from '../app/app.state';
import { PatientState, PatientStateModel } from '../patient/patient.state';
import { LookupState } from '../lookup/lookup.state';
import {
  FetchPreviewPatient,
  GetPatients,
  LoadPatientData,
  LoginPatient,
  SetLastSelectedPatientId,
  SetSelectedPatientId,
  SetPatientIsLoading,
} from '../patient/patient.actions';
import { HttpErrorResponse } from '@angular/common/http';
import { TestPatientsState } from '../test-patients/test-patients.state';
import { AgentState } from '../agent/agent.state';
import { ResetTestPatient } from '../test-patients/test-patients.actions';

export interface UserStateModel {
  authenticatedUser: AuthenticatedUser | null;
  ipAddress: ApiStoreData<IpAddress | null>;
  isAuthenticated: boolean;
  isSessionExpired: boolean;
  isSigningIn: boolean;
  isSigningOut: boolean;
  isActivating: boolean;
  token: string | null;
  updatePassword: ApiStoreData<null>;
}

export const userDefaults = {
  authenticatedUser: null,
  ipAddress: defaultApiStoreData,
  isAuthenticated: false,
  isSessionExpired: false,
  isSigningOut: false,
  isSigningIn: false,
  isActivating: false,
  token: null,
  updatePassword: defaultApiStoreData,
};

@State<UserStateModel>({
  name: 'user',
  defaults: userDefaults,
})
@Injectable({ providedIn: 'root' })
export class UserState {
  authStateSubscription: Subscription | null = null;

  constructor(
    private apiService: ApiService,
    private authStateService: OktaAuthStateService,
    private ipAddressService: IpAddressService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private router: Router,
    private sentry: SentryService,
    private sessionExpirationService: SessionExpirationService,
    private store: Store
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static authenticatedUser(state: UserStateModel): AuthenticatedUser | null {
    return state.authenticatedUser;
  }

  @Selector()
  static ipAddress(state: UserStateModel): string | null {
    return state.ipAddress.data?.ip || null;
  }

  @Selector()
  static isAgentUser(state: UserStateModel): boolean {
    return state.authenticatedUser?.role === UserRole.Agent;
  }

  @Selector()
  static isAuthenticated(state: UserStateModel): boolean {
    return state.isAuthenticated;
  }

  @Selector()
  static isSessionExpired(state: UserStateModel): boolean {
    return state.isSessionExpired;
  }

  @Selector()
  static isTestUser(state: UserStateModel): boolean {
    return state.authenticatedUser?.role === UserRole.Test;
  }

  @Selector([PatientState])
  static patientSelectionRequired(state: UserStateModel, patientState: PatientStateModel): boolean {
    return (
      state.authenticatedUser?.role === UserRole.Patient &&
      patientState.patients.data.length > 1 &&
      (!patientState.lastSelectedPatientId ||
        !patientState.patients.data.some((p) => p.id === patientState.lastSelectedPatientId))
    );
  }

  @Selector()
  static token(state: UserStateModel): string | null {
    return state.token;
  }

  @Selector()
  static updatePasswordState(state: UserStateModel): ApiStoreData<null> {
    return state.updatePassword;
  }

  @Selector()
  static isActivating(state: UserStateModel): boolean {
    return state.isActivating;
  }

  @Action(ClearAuthStateSubscription)
  clearAuthStateSubscription() {
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
  }

  @Action(FetchIpAddress)
  fetchIpAddress(ctx: StateContext<UserStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.ipAddress.isLoading = true;
      })
    );

    return this.ipAddressService.getIpAddress().pipe(
      tap({
        next: (response) => {
          this.handleSuccess({ ctx, data: response, stateKey: 'ipAddress' });
        },
      }),
      catchError(
        (error: HttpErrorResponse): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'ipAddress', skipRecordingError: true })
      )
    );
  }

  @Action(FetchPortalUser)
  fetchPortalUser(ctx: StateContext<UserStateModel>) {
    ctx.dispatch(new SetPatientIsLoading(true));

    const userRole = this.store.selectSnapshot(UserState.authenticatedUser)?.role;

    if (userRole !== UserRole.Patient) {
      const patientId = this.store.selectSnapshot(
        userRole === UserRole.Test ? TestPatientsState.testPatientId : AgentState.agentPreviewPatientId
      );

      if (patientId) {
        return ctx.dispatch(new FetchPreviewPatient(patientId));
      } else {
        return ctx.dispatch(new SetPatientIsLoading(false));
      }
    }

    const lastSelectedPatientId = this.store.selectSnapshot(PatientState.lastSelectedPatientId);
    const isActivating = this.store.selectSnapshot(UserState.isActivating);

    return ctx.dispatch(new FetchIpAddress()).subscribe(() => {
      const ipAddress = ctx.getState().ipAddress.data?.ip || 'Unknown';

      if (isActivating) {
        ctx.dispatch(new ActivateUser()).subscribe({
          next: () => {
            ctx.dispatch(new GetPatients(ipAddress, lastSelectedPatientId));
          },
          error: () => {
            ctx.dispatch(new GetPatients(ipAddress, lastSelectedPatientId));
          },
        });
      } else {
        ctx.dispatch(new GetPatients(ipAddress, lastSelectedPatientId));
      }
    });
  }

  @Action(ActivateUser)
  activateUser(ctx: StateContext<UserStateModel>) {
    return this.apiService.activateUser().subscribe({
      next: () => {
        ctx.dispatch(new SetIsActivating(false));
      },
      error: (error: HttpErrorResponse) => {
        this.apiService.activateUser().subscribe({
          next: () => {
            ctx.dispatch(new SetIsActivating(false));
          },
        });
      },
    });
  }

  @Action(InitUser)
  initUser(ctx: StateContext<UserStateModel>) {
    // This lets the authStateService to initialize before we subscribe to it
    // Otherwise the first authState is always isAuthenticated: false
    setTimeout(() => {
      this.authStateSubscription = this.authStateService.authState$.subscribe((authState: AuthState) => {
        const { accessToken, isAuthenticated } = authState;

        const lastToken: string | undefined = ctx.getState().token?.split(' ')[1];

        // Even if we are using id token, we must check if the id token is set as Okta only resets access token when signing out
        const token = accessToken?.accessToken;

        const role = ctx.getState().authenticatedUser?.role;

        if (role === UserRole.Test) {
          const testAuthenticatedUser = {
            email: 'testPatient@email.com',
            oktaId: 'test-okta-id',
            role: UserRole.Test,
          };

          ctx.dispatch(new SetAuthenticatedUser(testAuthenticatedUser));
          return;
        }

        const isSigningIn = !!token && !lastToken;
        const isTokenCleared = !token && !!lastToken;
        const isTokenChanged = !!token && token !== lastToken;
        const isSigningOut = ctx.getState().isSigningOut;
        const signInInitiatedInThisTab = ctx.getState().isSigningIn;

        if (role === UserRole.Agent && !isTokenChanged) {
          return;
        }

        ctx.setState(
          produce((draft) => {
            draft.isAuthenticated = isAuthenticated || false;
            draft.token = token ? `Bearer ${token}` : null;

            if (token) {
              draft.isSessionExpired = false;
              draft.isSigningOut = false;
            }
          })
        );

        const userInfo: UserClaims | undefined = accessToken?.claims;

        const patientAuthenticatedUser = userInfo
          ? {
              email: userInfo.sub,
              oktaId: userInfo['uid'] as string,
              role: UserRole.Patient,
            }
          : null;

        ctx.dispatch(new SetAuthenticatedUser(patientAuthenticatedUser));

        if ((isTokenCleared && !isSigningOut) || isTokenChanged || (isSigningIn && !signInInitiatedInThisTab)) {
          if (isSigningIn) {
            this.sessionExpirationService.lastAction = Date.now();

            ctx.setState(
              produce((draft) => {
                draft.isSigningIn = false;
              })
            );
          }

          window.location.reload();
        }
      });
    }, 0);
  }

  @Action(ResetUser)
  resetUser(ctx: StateContext<UserStateModel>) {
    ctx.setState(
      produce((draft) => {
        Object.assign(draft, userDefaults);
      })
    );

    this.sentry.setUserRole(null);
    this.oktaAuth.revokeAccessToken().then(() => {
      this.oktaAuth.closeSession();
    });
  }

  @Action(SetAgentAuthValues)
  setAgentAuthValues(ctx: StateContext<UserStateModel>, action: SetAgentAuthValues) {
    ctx.setState(
      produce((draft) => {
        draft.token = `Bearer ${action.token}`;
        draft.isAuthenticated = true;
        draft.isSessionExpired = false;
        draft.isSigningOut = false;
      })
    );

    this.sessionExpirationService.lastAction = Date.now();
  }

  @Action(SetAuthenticatedUser)
  setAuthenticatedUser(ctx: StateContext<UserStateModel>, action: SetAuthenticatedUser) {
    ctx.setState(
      produce((draft) => {
        draft.authenticatedUser = action.authenticatedUser;
      })
    );

    this.sentry.setUserRole(action.authenticatedUser?.role || null);
  }

  @Action(SetUserSignInFlag)
  setUserSignInFlag(ctx: StateContext<UserStateModel>, action: SetUserSignInFlag) {
    ctx.setState(
      produce((draft) => {
        draft.isSigningIn = action.value;
      })
    );
  }

  @Action(SignInTestPatient)
  signInTestPatient(ctx: StateContext<UserStateModel>, action: SignInTestPatient) {
    ctx.dispatch(new ResetUser()).subscribe(() => {
      ctx.setState(
        produce((draft) => {
          draft.token = action.token;
          draft.isAuthenticated = true;
          draft.isSessionExpired = false;
        })
      );

      this.sessionExpirationService.lastAction = Date.now();

      const testAuthenticatedUser = {
        email: 'testPatient@email.com',
        oktaId: 'test-okta-id',
        role: UserRole.Test,
      };

      this.store.dispatch(new SetAuthenticatedUser(testAuthenticatedUser));
    });
  }

  @Action(ResetState)
  resetState(ctx: StateContext<UserStateModel>) {
    const lastSelectedPatientId = this.store.selectSnapshot(PatientState.lastSelectedPatientId);

    ctx.dispatch([new StateResetAll(AppState), new ResetUser(), new ResetTestPatient()]).subscribe(() => {
      if (lastSelectedPatientId) {
        ctx.dispatch(new SetLastSelectedPatientId(lastSelectedPatientId));
      }
    });
  }

  @Action(SignOut)
  signOut(ctx: StateContext<UserStateModel>, action: SignOut) {
    console.log('401 Logging: SignOut Action triggered in App Store...');

    if (this.store.selectSnapshot(UserState.isTestUser)) {
      ctx.setState(
        produce((draft) => {
          draft.token = null;
          draft.isAuthenticated = false;
          draft.isSessionExpired = action.options?.sessionExpired || false;
          draft.isSigningOut = true;
        })
      );

      this.router.navigate(['/login']).then(() => {
        setTimeout(() => {
          this.store.dispatch(new ResetState());
        }, 800);
      });
    } else {
      this.router.navigate(['/login']);

      ctx.setState(
        produce((draft) => {
          draft.isSessionExpired = action.options?.sessionExpired || false;
          draft.isSigningOut = true;
        })
      );

      this.store.dispatch(new ResetState());

      this.oktaAuth.revokeAccessToken().then(() => {
        this.oktaAuth.closeSession();
      });
    }
  }

  @Action(SwitchPatient)
  switchPatient(ctx: StateContext<UserStateModel>, action: SwitchPatient) {
    this.router.navigate(['/']).then(() => {
      ctx.dispatch(new SetSelectedPatientId(action.patientId));
      ctx.dispatch([
        new SetPatientIsLoading(true),
        new StateResetAll(AppState, UserState, PatientState, LookupState),
        new LoginPatient(this.store.selectSnapshot(UserState.ipAddress) || 'Unknown', action.patientId),
        new LoadPatientData(),
      ]);
    });
  }

  @Action(UpdatePassword)
  updatePassword(ctx: StateContext<UserStateModel>, action: UpdatePassword) {
    ctx.setState(
      produce((draft) => {
        draft.updatePassword.isLoading = true;
      })
    );

    const oktaId = ctx.getState().authenticatedUser?.oktaId;

    if (!oktaId) {
      const error = new MissingApiRequestDataError(['Missing user Okta ID']);
      return this.handleError({ ctx, error, stateKey: 'updatePassword' });
    }

    const body = buildUpdatePasswordRequest(oktaId, action.formValues);

    return this.apiService.updatePassword(body).pipe(
      tap({
        next: () => {
          this.apiService.revokePreviousTokens().subscribe();
          this.handleSuccess({ ctx, stateKey: 'updatePassword' });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({
            ctx,
            error,
            stateKey: 'updatePassword',
            resetData: false,
            skipRecordingError: error.message.toLowerCase().includes('password change unsuccessful'),
          })
      )
    );
  }

  @Action(SetIsActivating)
  setIsActivating(ctx: StateContext<UserStateModel>, action: SetIsActivating) {
    ctx.setState(
      produce((draft) => {
        draft.isActivating = action.values;
      })
    );
  }

  @Action(verifyActivationToken)
  verifyActivationToken(ctx: StateContext<UserStateModel>, action: verifyActivationToken) {
    return this.apiService.verifyActivationToken(action.token);
  }

  @Action(resendActivationEmail)
  resendActivationEmail(ctx: StateContext<UserStateModel>, action: resendActivationEmail) {
    return this.apiService.resendActivationEmail(action.token);
  }

  @Action(createUserFromEmail)
  createUserFromEmail(ctx: StateContext<UserStateModel>, action: createUserFromEmail) {
    return this.apiService.createUserFromEmail(action.email);
  }
}
